var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { MIN_STUDY_SEARCH_LENGTH, ProjectType, StudyStatus, StudyType, StudyVersionStatus, } from "healthex-common/study.dtos";
import { deepCopy, enumValues, filterFalse, genId, willNeverHappen, withoutUndefinedProperties } from "healthex-common/utils";
import { format } from "date-fns";
import { StudyStatusUI, getFilterFromStudyStatusUI, getStudyStatusUI } from "./StudyStatusHelpers";
import { defaultPageSize } from "healthex-common/constants";
import debounce from "debounce-promise";
import { NamedRoutes } from "./NamedRoutes";
import { colors } from "healthex-common/colors";
import React from "react";
import Edit from "@mui/icons-material/Edit";
import MiscellaneousServices from "@mui/icons-material/MiscellaneousServices";
import { i18nGetDateFnsLocale } from "../Infra/i18next";
import { Disposer } from "../Infra/Disposer";
export var HXDesktopStudiesSearchType;
(function (HXDesktopStudiesSearchType) {
    HXDesktopStudiesSearchType["MY_PROJECTS"] = "MY_PROJECTS";
    HXDesktopStudiesSearchType["OTHER_PROJECTS"] = "OTHER_PROJECTS";
})(HXDesktopStudiesSearchType || (HXDesktopStudiesSearchType = {}));
export class HXDesktopStudiesSearchViewModel {
    constructor(props) {
        this.activeStudiesOnly = false;
        this.showTabs = false;
        this.disposer = new Disposer();
        this.studies = NOT_LOADED;
        this.studiesRequiringAction = NOT_LOADED;
        this.studiesSummary = NOT_LOADED;
        this.defaultSearchType = HXDesktopStudiesSearchType.MY_PROJECTS;
        this.searchInput = {
            studyStatus: StudyStatusUI.ACTIVE,
            text: "",
        };
        this.totalStudies = 0;
        this.page = 0;
        this.showAllStudiesRequiringAction = false;
        this.loadedSearchType = null;
        this.studiesLoading = false;
        this.currentStudyLoadingId = "";
        this.currentStudiesRequiringActionLoadingId = "";
        this.currentSummaryLoadingId = "";
        this.loadStudiesDebounced = debounce((paginationParams) => this.loadStudies(paginationParams), 500);
        this.studyManager = props.container.studyManager;
        this.routingUIManager = props.container.routingUIManager;
        this.authManager = props.container.authManager;
        this.addProjectUIManager = props.container.addProjectUIManager;
        this.defaultSearchType = props.defaultSearchType;
        this.activeStudiesOnly = !!props.activeStudiesOnly;
        this.showTabs = !!props.showTabs;
        this.onRowClick = props.onRowClick;
    }
    run() {
        this.disposer.reaction(() => this.searchType, () => {
            void this.loadStudiesAndStudiesRequiringAction({ page: this.page, pageSize: defaultPageSize });
            void this.loadStudySummary();
        }, { fireImmediately: true, name: "searchType" });
        this.disposer.reaction(() => [this.searchInput], () => {
            void this.loadStudiesDebounced({ page: this.page, pageSize: defaultPageSize });
        }, { name: "searchInput" });
    }
    stop() {
        this.disposer.dispose();
    }
    get searchType() {
        var _a;
        const routeName = (_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName;
        if (routeName === NamedRoutes.MY_PROJECTS.routeName) {
            return HXDesktopStudiesSearchType.MY_PROJECTS;
        }
        else if (routeName === NamedRoutes.OTHER_PROJECTS.routeName) {
            return HXDesktopStudiesSearchType.OTHER_PROJECTS;
        }
        else {
            return this.defaultSearchType;
        }
    }
    get tabsToShow() {
        if (!this.showTabs)
            return [];
        const researcher = this.authManager.isResearcher;
        return filterFalse([researcher && HXDesktopStudiesSearchType.MY_PROJECTS, HXDesktopStudiesSearchType.OTHER_PROJECTS]);
    }
    get tabsToBadge() {
        const result = [];
        const researcher = this.authManager.isResearcher;
        const studiesRequiringAction = this.studiesRequiringAction !== NOT_LOADED && this.studiesRequiringAction.length > 0;
        if (researcher && studiesRequiringAction) {
            result.push(HXDesktopStudiesSearchType.MY_PROJECTS);
        }
        return result;
    }
    get showTabBar() {
        if (!this.showTabs)
            return false;
        return this.tabsToShow.length > 1;
    }
    get studiesRequiringActionToShow() {
        if (this.studiesRequiringAction === NOT_LOADED)
            return NOT_LOADED;
        if (this.showAllStudiesRequiringAction) {
            return this.studiesRequiringAction;
        }
        else {
            return this.studiesRequiringAction.slice(0, 3);
        }
    }
    get showAllStudiesRequiringActionVisible() {
        return this.studiesRequiringAction !== NOT_LOADED && this.studiesRequiringAction.length > 3;
    }
    get showMyStudiesZeroState() {
        if (this.loadedSearchType !== HXDesktopStudiesSearchType.MY_PROJECTS)
            return false;
        if (this.studiesSummaryFormatted === NOT_LOADED)
            return false;
        return this.getStudySummaryForSearchType(HXDesktopStudiesSearchType.MY_PROJECTS) === 0;
    }
    get showMyStudiesNoPostDraftZeroState() {
        if (this.loadedSearchType !== HXDesktopStudiesSearchType.MY_PROJECTS)
            return false;
        if (this.studiesSummary === NOT_LOADED)
            return false;
        return this.studiesSummary[HXDesktopStudiesSearchType.MY_PROJECTS] === 0;
    }
    get showMyOrgStudiesZeroState() {
        if (this.loadedSearchType !== HXDesktopStudiesSearchType.OTHER_PROJECTS)
            return false;
        if (this.studiesSummaryFormatted === NOT_LOADED)
            return false;
        return this.getStudySummaryForSearchType(HXDesktopStudiesSearchType.OTHER_PROJECTS) === 0;
    }
    get showChevronColumn() {
        return this.searchType === HXDesktopStudiesSearchType.MY_PROJECTS || this.searchType === HXDesktopStudiesSearchType.OTHER_PROJECTS;
    }
    get orgListZeroStateTitle() {
        const includeOther = this.authManager.isResearcher;
        return includeOther ? "No other projects" : "No projects";
    }
    get internallySponsoredDisplayValue() {
        return `${this.authManager.orgName} (internally-sponsored)`;
    }
    async loadStudiesAndStudiesRequiringAction(paginationParams) {
        this.studies = NOT_LOADED;
        await Promise.all([this.loadStudiesRequiringAction(), this.loadStudies(paginationParams)]);
        this.loadedSearchType = this.searchType;
    }
    async loadStudies(paginationParams) {
        const orgId = this.authManager.orgId;
        if (!orgId)
            return;
        this.studiesLoading = true;
        const undefinedIfEmpty = (input) => (input !== "" ? input : undefined);
        const tabSearchInput = this.getSearchTypeInput(this.searchType);
        const { page, pageSize } = paginationParams;
        let searchText = this.searchInput.text ? this.searchInput.text.trim() : "";
        if (searchText.length < MIN_STUDY_SEARCH_LENGTH) {
            searchText = "";
        }
        const searchInput = {
            ...getFilterFromStudyStatusUI(this.searchInput.studyStatus),
            text: undefinedIfEmpty(searchText),
            projectTypes: [ProjectType.GENERAL_DATA_USE],
        };
        const studyLoadingId = genId(36);
        this.currentStudyLoadingId = studyLoadingId;
        const { results, total } = await this.studyManager.fetchStudies(StudyVersionStatus.ACTIVE, {
            ...searchInput,
            start: page * pageSize,
            end: page * pageSize + pageSize,
            ...withoutUndefinedProperties(tabSearchInput),
        });
        if (studyLoadingId !== this.currentStudyLoadingId) {
            return;
        }
        const studies = results;
        this.totalStudies = total;
        this.studies = studies.map((study) => ({
            ...study,
            sponsor: this.getSponsor(study),
            lastActivityInfo: this.getLastActivityInfo(study),
            studyStatusUI: getStudyStatusUI(study),
        }));
        this.studiesLoading = false;
    }
    async loadStudiesRequiringAction() {
        if (!this.authManager.orgId || !this.authManager.id)
            return;
        const studiesRequiringActionLoadingId = genId(36);
        this.currentStudiesRequiringActionLoadingId = studiesRequiringActionLoadingId;
        const finishAddingInput = {
            hasUser: { userId: this.authManager.id },
            addedByOrganizationId: this.authManager.orgId,
        };
        const studiesRequiringAction = (await this.studyManager.fetchStudies(StudyVersionStatus.DRAFT, finishAddingInput)).results;
        if (studiesRequiringActionLoadingId !== this.currentStudiesRequiringActionLoadingId) {
            return;
        }
        this.studiesRequiringAction = studiesRequiringAction;
    }
    async loadStudySummary() {
        if (!this.authManager.orgId)
            return;
        const summaryLoadingId = genId(36);
        this.currentSummaryLoadingId = summaryLoadingId;
        const summary = await this.studyManager.fetchStudiesTotal(StudyVersionStatus.ACTIVE, {
            [HXDesktopStudiesSearchType.MY_PROJECTS]: this.getSearchTypeInput(HXDesktopStudiesSearchType.MY_PROJECTS),
            [HXDesktopStudiesSearchType.OTHER_PROJECTS]: this.getSearchTypeInput(HXDesktopStudiesSearchType.OTHER_PROJECTS),
        });
        if (summaryLoadingId !== this.currentSummaryLoadingId) {
            return;
        }
        this.studiesSummary = summary;
    }
    getSearchTypeInput(searchType) {
        if (!this.authManager.id || !this.authManager.orgId)
            throw new Error("Not logged in");
        const activeStudyExcludeStatuses = this.activeStudiesOnly ? [StudyStatus.WITHDRAWN, StudyStatus.COMPLETED] : [];
        switch (searchType) {
            case HXDesktopStudiesSearchType.MY_PROJECTS:
                return {
                    hasUser: { userId: this.authManager.id },
                    excludeStatuses: [StudyStatus.DRAFT, ...activeStudyExcludeStatuses],
                    excludePaused: this.activeStudiesOnly,
                };
            case HXDesktopStudiesSearchType.OTHER_PROJECTS: {
                const excludeHasUser = this.tabsToShow.includes(HXDesktopStudiesSearchType.MY_PROJECTS)
                    ? { userId: this.authManager.id }
                    : undefined;
                return {
                    addedByOrganizationId: this.authManager.orgId,
                    excludeStatuses: [StudyStatus.DRAFT, ...activeStudyExcludeStatuses],
                    excludeHasUser,
                    excludePaused: this.activeStudiesOnly,
                };
            }
        }
        return willNeverHappen(searchType);
    }
    getSponsor(study) {
        var _a, _b;
        return { name: (_a = study.sponsorOrganizationName) !== null && _a !== void 0 ? _a : null, isMyOrg: (_b = study.isSponsor) !== null && _b !== void 0 ? _b : false };
    }
    getLastActivityInfo(study) {
        if (!study.lastActivity || !study.lastActivityUser) {
            return undefined;
        }
        return { date: format(new Date(study.lastActivity), "LLL dd, yyyy"), user: study.lastActivityUser };
    }
    get studiesSummaryFormatted() {
        const studiesSummaryFormatted = deepCopy(this.studiesSummary);
        if (studiesSummaryFormatted === NOT_LOADED || this.studiesRequiringAction === NOT_LOADED)
            return NOT_LOADED;
        if (studiesSummaryFormatted[HXDesktopStudiesSearchType.MY_PROJECTS] !== undefined) {
            studiesSummaryFormatted[HXDesktopStudiesSearchType.MY_PROJECTS] =
                studiesSummaryFormatted[HXDesktopStudiesSearchType.MY_PROJECTS] + this.studiesRequiringAction.length;
        }
        return studiesSummaryFormatted;
    }
    getStudySummaryForSearchType(searchType) {
        var _a;
        if (this.studiesSummaryFormatted === NOT_LOADED || this.studiesRequiringAction === NOT_LOADED)
            return NOT_LOADED;
        return (_a = this.studiesSummaryFormatted[searchType]) !== null && _a !== void 0 ? _a : 0;
    }
    toggleShowAllStudiesRequiringAction() {
        this.showAllStudiesRequiringAction = !this.showAllStudiesRequiringAction;
    }
    isMyOrganization(organization) {
        return organization.id === this.authManager.orgId;
    }
    getStudyProgress(study) {
        var _a;
        return Math.min(99, (_a = this.addProjectUIManager.getOverallProgressForStudy(study)) !== null && _a !== void 0 ? _a : 0);
    }
    continueAddingStudy(study) {
        this.routingUIManager.navigateTo(NamedRoutes.PROJECTS_ADD_PROJECT, { projectId: study.id });
    }
    compareStatus(a, b) {
        return enumValues(StudyStatusUI).indexOf(a) - enumValues(StudyStatusUI).indexOf(b);
    }
    getStatusColor(status) {
        switch (status) {
            case StudyStatus.DRAFT:
                return colors.chips_slate;
            case StudyStatus.ACTIVE:
                return colors.chips_amethyst;
            case StudyStatus.COMPLETED:
                return colors.chips_forest;
            case StudyStatus.WITHDRAWN:
                return colors.chips_tulip;
        }
        return willNeverHappen(status);
    }
    compareStudyType(a, b) {
        return enumValues(StudyType).indexOf(a) - enumValues(StudyType).indexOf(b);
    }
    onRowClicked(study) {
        this.onRowClick({ studyId: study.id, searchType: this.searchType });
    }
    updateSearchInput(update) {
        this.searchInput = { ...this.searchInput, ...update };
    }
    async updatePagination(paginationParams) {
        this.page = paginationParams.page;
        await this.loadStudies(paginationParams);
    }
    setTab(tab) {
        if (tab === HXDesktopStudiesSearchType.MY_PROJECTS) {
            this.routingUIManager.navigateTo(NamedRoutes.MY_PROJECTS);
        }
        else if (tab === HXDesktopStudiesSearchType.OTHER_PROJECTS) {
            this.routingUIManager.navigateTo(NamedRoutes.OTHER_PROJECTS);
        }
        else {
            willNeverHappen(tab);
        }
    }
    getNumberInTab(tab) {
        var _a;
        if (this.studiesSummaryFormatted === NOT_LOADED || this.studiesRequiringAction === NOT_LOADED)
            return NOT_LOADED;
        return (_a = this.studiesSummaryFormatted[tab]) !== null && _a !== void 0 ? _a : 0;
    }
    getStudiesRequiringActionRelevantDateDescription(study) {
        if (study.status === StudyStatus.DRAFT) {
            return `Added ${format(study.createdAt, "LLL d, yyyy", {
                locale: i18nGetDateFnsLocale(),
            })}`;
        }
        else {
            if (!study.firstActivatedAt)
                return null;
            return `Originally activated on ${format(study.firstActivatedAt, "LLL d, yyyy", {
                locale: i18nGetDateFnsLocale(),
            })}`;
        }
    }
    getShowStudyProgress(study) {
        return study.status === StudyStatus.DRAFT;
    }
    getStudiesRequiringActionButtonIcon(study) {
        if (study.status === StudyStatus.DRAFT) {
            return React.createElement(MiscellaneousServices);
        }
        else {
            return React.createElement(Edit);
        }
    }
    getStudiesRequiringActionButtonText(study) {
        if (study.status === StudyStatus.DRAFT) {
            return "Continue setup";
        }
        else {
            return "Continue editing";
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], HXDesktopStudiesSearchViewModel.prototype, "studies", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], HXDesktopStudiesSearchViewModel.prototype, "studiesRequiringAction", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], HXDesktopStudiesSearchViewModel.prototype, "studiesSummary", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], HXDesktopStudiesSearchViewModel.prototype, "defaultSearchType", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], HXDesktopStudiesSearchViewModel.prototype, "searchInput", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], HXDesktopStudiesSearchViewModel.prototype, "totalStudies", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], HXDesktopStudiesSearchViewModel.prototype, "page", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], HXDesktopStudiesSearchViewModel.prototype, "showAllStudiesRequiringAction", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], HXDesktopStudiesSearchViewModel.prototype, "loadedSearchType", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], HXDesktopStudiesSearchViewModel.prototype, "studiesLoading", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], HXDesktopStudiesSearchViewModel.prototype, "currentStudyLoadingId", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], HXDesktopStudiesSearchViewModel.prototype, "currentStudiesRequiringActionLoadingId", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], HXDesktopStudiesSearchViewModel.prototype, "currentSummaryLoadingId", void 0);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "searchType", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "tabsToShow", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "tabsToBadge", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "showTabBar", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "studiesRequiringActionToShow", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "showAllStudiesRequiringActionVisible", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "showMyStudiesZeroState", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "showMyStudiesNoPostDraftZeroState", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "showMyOrgStudiesZeroState", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "showChevronColumn", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "orgListZeroStateTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "internallySponsoredDisplayValue", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], HXDesktopStudiesSearchViewModel.prototype, "studiesSummaryFormatted", null);
